import { AdhocBatchConfirmationModal } from '@/components/Payouts/AdhocBatchConfirmationModal';
import {
  AdhocBatchFields,
  AdhocBatchFormFields,
  CUTOFF_DISPLAY_FORMAT_WITH_TZ,
} from '@/components/Payouts/AdhocBatchFormFields';
import { batchTypeLabel } from '@/components/Payouts/batchTypeOptions';
import { currencyFormatter } from '@/helpers/currencyFormatter';
import { DATE_FORMAT } from '@/helpers/dates';
import { isErrorWithData, isErrorWithErrors } from '@/services/helpers';
import {
  useCreateAdhocBatchMutation,
  useGetBatchGroupsQuery,
  usePreviewAdhocBatchMutation,
} from '@/services/payouts';
import { PayoutTransactionClientData } from '@/types/Payouts';
import {
  Alert,
  Box,
  Button,
  FormikRadioGroup,
  FormikSelectInputNative,
  Modal,
  toast,
} from '@palmetto/palmetto-components';
import { Field, Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

import {
  AdhocBatchPreviewResult,
  BatchReviewStatus,
  BatchType,
  CreateAdhocBatchPayload,
  IsoDateString,
  IsoDateTimeString,
} from 'types';

const BATCH_DATE_DISPLAY_FORMAT = 'LLL d, yyyy';

/** `getBatchGroups` has no id of its own; date + type is what identifies a group. */
const toBatchKey = (batchDate: IsoDateString, batchType: BatchType) => `${batchDate}|${batchType}`;

const fromBatchKey = (key: string) => {
  const [batchDate, batchType] = key.split('|');
  return { batchDate: batchDate as IsoDateString, batchType: batchType as BatchType };
};

type TargetChoice = 'existing' | 'new';

interface AddToBatchTargetFormValues extends AdhocBatchFields {
  target: TargetChoice;
  /** `${batchDate}|${batchType}` of the chosen existing batch group. */
  existingBatchKey: string;
}

const validationSchema = yup.object({
  target: yup.string().oneOf(['existing', 'new']).required(),
  existingBatchKey: yup.string().when('target', {
    is: 'existing',
    then: (schema) => schema.required('Select a batch.'),
    otherwise: (schema) => schema.optional(),
  }),
  batchType: yup.string().when('target', {
    is: 'new',
    then: (schema) => schema.required('Batch type is a required field.'),
    otherwise: (schema) => schema.optional(),
  }),
  batchDate: yup.string().when('target', {
    is: 'new',
    then: (schema) =>
      schema
        .required('Batch date is a required field.')
        // Non-empty is not enough: an unparseable string would pass `.required` but produce a null
        // ISO date on submit, silently no-op'ing instead of showing the user an error.
        .test('validDate', 'Batch date is not a valid date.', (value) =>
          value ? DateTime.fromFormat(value, DATE_FORMAT).isValid : false,
        ),
    otherwise: (schema) => schema.optional(),
  }),
  autoFill: yup.boolean(),
  cutoffDateTimeIso: yup.string().when(['target', 'autoFill'], {
    is: (target: string, autoFill: boolean) => target === 'new' && autoFill,
    then: (schema) =>
      schema
        .required('Cutoff date & time is a required field.')
        .test('notInFuture', 'Cutoff date & time cannot be in the future.', (value) =>
          value ? DateTime.fromISO(value) <= DateTime.now() : false,
        ),
    otherwise: (schema) => schema.optional(),
  }),
});

interface TargetBatchOption {
  value: string;
  label: string;
}

/** Stable default so an omitted `selectedIds` doesn't change identity on every parent render. */
const NO_SELECTED_IDS: string[] = [];

/**
 * Distinguishes "still loading", "failed", and a genuinely empty list — a bare empty select would
 * read as "there are no batches" in all three cases.
 *
 * The failure case is surfaced as an Alert rather than the field's `error`, because
 * FormikSelectInputNative overwrites `error` with the field's own validation state.
 */
interface BatchSelectStatus {
  helpText: string;
  disabled: boolean;
  /** `null` once an existing batch is selectable. Distinguishes the three disabled states. */
  reason: 'loading' | 'error' | 'empty' | null;
}

const batchSelectStatus = (isLoading: boolean, isError: boolean, optionCount: number): BatchSelectStatus => {
  if (isLoading) return { helpText: 'Loading batches…', disabled: true, reason: 'loading' };
  if (isError) return { helpText: 'Batches could not be loaded.', disabled: true, reason: 'error' };
  if (!optionCount) {
    return {
      helpText: 'No other queued batches available. Create a new ad hoc batch instead.',
      disabled: true,
      reason: 'empty',
    };
  }
  return { helpText: 'Showing queued and pending batches.', disabled: false, reason: null };
};

const AddToBatchTargetFormBody = ({
  batchOptions,
  selectionCount,
  selectionTotal,
  batchSelect,
}: {
  batchOptions: TargetBatchOption[];
  selectionCount: number;
  selectionTotal: number;
  batchSelect: BatchSelectStatus;
}) => {
  const { values, isSubmitting, setFieldValue } = useFormikContext<AddToBatchTargetFormValues>();

  // Once the list has settled with nothing to pick, 'existing' is a dead default: it is disabled, so
  // the user cannot leave it and cannot submit from it. Advance for them. Deliberately skipped while
  // loading, where the outcome is still unknown and flipping would fight a user who wants a batch.
  const shouldLeaveExisting =
    values.target === 'existing' && (batchSelect.reason === 'error' || batchSelect.reason === 'empty');
  useEffect(() => {
    if (shouldLeaveExisting) void setFieldValue('target', 'new');
  }, [shouldLeaveExisting, setFieldValue]);

  // Picking an existing batch is impossible while the list is loading, failed, or empty — disable the
  // choice rather than letting the user submit into a required field they cannot fill.
  const targetOptions = useMemo(
    () => [
      { id: 'target-existing', value: 'existing', label: 'An existing batch', disabled: batchSelect.disabled },
      { id: 'target-new', value: 'new', label: 'A new ad hoc batch' },
    ],
    [batchSelect.disabled],
  );

  return (
    <Box childGap="lg">
      <Box background="primary" radius="md" padding="md" fontSize="sm" color="body-secondary">
        <Box as="span">
          <Box as="span" fontWeight="bold" color="body-primary">
            {selectionCount} transaction{selectionCount === 1 ? '' : 's'}
          </Box>{' '}
          selected · {currencyFormatter.format(selectionTotal)} total
        </Box>
      </Box>

      {/* Sits outside the target branch: the auto-advance to 'new' unmounts that branch, and this is
          the only thing telling the user why their choice was made for them. */}
      {batchSelect.reason === 'error' || batchSelect.reason === 'empty' ? (
        <Alert
          variant={batchSelect.reason === 'error' ? 'danger' : 'warning'}
          hasIcon
          title={batchSelect.reason === 'error' ? 'Could not load batches' : 'No other queued batches available'}
          message="Adding to an existing batch is unavailable, so this will create a new ad hoc batch instead."
        />
      ) : null}

      <Field
        title="Add to"
        name="target"
        id="target"
        options={targetOptions}
        component={FormikRadioGroup}
        direction="column"
        isDisabled={isSubmitting}
      />

      {values.target === 'existing' ? (
        <Field
          label="Batch"
          name="existingBatchKey"
          id="existingBatchKey"
          component={FormikSelectInputNative}
          isRequired
          isDisabled={isSubmitting || batchSelect.disabled}
          placeholder="Select a batch…"
          options={batchOptions}
          helpText={batchSelect.helpText}
        />
      ) : (
        <AdhocBatchFormFields selectionCount={selectionCount} />
      )}
    </Box>
  );
};

interface ConfirmLabels {
  sourceLabel: string;
  sourceDetail: string;
  targetLabel: string;
  targetDetail: string;
}

/**
 * Renders inside `Formik` rather than via its render prop so `dismiss` and `handleConfirm` can read
 * the form bag from context and stay referentially stable across renders.
 */
const AddToBatchTargetModalContent = ({
  isOpen,
  modalTitle,
  confirmCopy,
  batchOptions,
  batchSelect,
  selectionCount,
  selectionTotal,
  preview,
  confirmLabels,
  sourceWillEmpty,
  isConfirmationOpen,
  isPreviewing,
  isCreating,
  closeConfirmation,
  handleClose,
  confirmMove,
}: {
  isOpen: boolean;
  modalTitle: string;
  confirmCopy: string;
  batchOptions: TargetBatchOption[];
  batchSelect: BatchSelectStatus;
  selectionCount: number;
  selectionTotal: number;
  preview: AdhocBatchPreviewResult | undefined;
  confirmLabels: ConfirmLabels;
  sourceWillEmpty: boolean;
  isConfirmationOpen: boolean;
  isPreviewing: boolean;
  isCreating: boolean;
  closeConfirmation: () => void;
  handleClose: () => void;
  /** Resolves true when the batch change was applied, so the form is only reset on success. */
  confirmMove: () => Promise<boolean>;
}) => {
  const { values, isSubmitting, resetForm, submitForm } = useFormikContext<AddToBatchTargetFormValues>();

  const dismiss = useCallback(() => {
    if (isSubmitting) return;
    closeConfirmation();
    resetForm();
    handleClose();
  }, [isSubmitting, closeConfirmation, resetForm, handleClose]);

  const handleConfirm = useCallback(async () => {
    if (await confirmMove()) resetForm();
  }, [confirmMove, resetForm]);

  return (
    <Form>
      <Modal isOpen={isOpen} maxWidth="4xl" ariaLabelledBy="addToBatchTargetModal" onDismiss={dismiss}>
        <Modal.Header id="addToBatchTargetModal" title={modalTitle} onDismiss={dismiss} />
        <Modal.Body background="secondary" childGap="lg">
          <AddToBatchTargetFormBody
            batchOptions={batchOptions}
            selectionCount={selectionCount}
            selectionTotal={selectionTotal}
            batchSelect={batchSelect}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            tone="neutral"
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            onClick={dismiss}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            isLoading={isSubmitting || isPreviewing}
            isDisabled={isSubmitting || isPreviewing || (values.target === 'existing' && batchSelect.disabled)}
            onClick={submitForm}
          >
            Review &amp; Confirm
          </Button>
        </Modal.Footer>
      </Modal>
      <AdhocBatchConfirmationModal
        isOpen={isConfirmationOpen}
        preview={preview}
        isLoading={isPreviewing}
        sourceLabel={confirmLabels.sourceLabel}
        sourceDetail={confirmLabels.sourceDetail}
        targetLabel={confirmLabels.targetLabel}
        targetDetail={confirmLabels.targetDetail}
        sourceWillEmpty={sourceWillEmpty}
        title={confirmCopy}
        confirmLabel={confirmCopy}
        onConfirm={handleConfirm}
        onCancel={closeConfirmation}
        isConfirming={isCreating}
      />
    </Form>
  );
};

/**
 * Picks the batch that selected transactions should end up in, then hands off to the shared
 * confirmation summary. Backs onto the ad hoc endpoints, which accept already-batched transactions
 * — so this reads as "add" from an unbatched list and as "move" (splitting a batch) from a queued
 * batch detail page.
 */
export const AddToBatchTargetModal = ({
  isOpen,
  handleClose,
  selectedIds = NO_SELECTED_IDS,
  transactions,
  onSuccess,
}: {
  isOpen: boolean;
  handleClose: () => void;
  selectedIds?: string[];
  transactions?: PayoutTransactionClientData[];
  onSuccess?: () => void;
}) => {
  const { batchDate: sourceBatchDate } = useParams<{ batchDate: IsoDateString }>();
  const [searchParams] = useSearchParams();
  const sourceBatchType = searchParams.get('batchType') as BatchType | null;

  const [createAdhocBatch, { isLoading: isCreating }] = useCreateAdhocBatchMutation();
  const [previewAdhocBatch, { isLoading: isPreviewing }] = usePreviewAdhocBatchMutation();
  // `rejected` is a sentinel, not a literal status: getBatches translates it to
  // `review.status != approved`, which is what "queued and pending" means here — those batches carry
  // no `review` field at all, so no exact status matches them. Same value the Queued tab passes.
  const {
    data: batchGroups,
    isLoading: isLoadingBatchGroups,
    isError: hasBatchGroupsError,
  } = useGetBatchGroupsQuery({
    batchReviewStatus: BatchReviewStatus.rejected,
    // A batch is addressed by date *and* type (`group-batches/:batchDate?batchType=`), so off a batch
    // detail page only same-type batches are sensible targets. Mixing organization and material
    // vendor batches in one list makes it easy to pick the wrong one.
    ...(sourceBatchType ? { batchType: sourceBatchType } : {}),
  });

  const [preview, setPreview] = useState<AdhocBatchPreviewResult | undefined>();
  const [pendingPayload, setPendingPayload] = useState<CreateAdhocBatchPayload | undefined>();
  const [confirmLabels, setConfirmLabels] = useState<ConfirmLabels>({
    sourceLabel: '',
    sourceDetail: '',
    targetLabel: '',
    targetDetail: '',
  });

  const isConfirmationOpen = Boolean(pendingPayload);

  // Snapshot the selection present when the modal opened. The parent re-creates the `selectedIds`
  // array reference on every render, so depending on it here would wipe partially-filled fields
  // mid-edit. The modal mounts fresh on open, so the mount-time selection is the correct set.
  const initialSelectedIdsRef = useRef(selectedIds);
  const selectedIdsAtOpen = initialSelectedIdsRef.current;

  // Frozen alongside the ids for the same reason: the parent keeps refetching while the modal is
  // open, and a live total sitting next to a frozen count would contradict it — a row that leaves the
  // list would drop the amount while the "N selected" beside it stayed put.
  const transactionsAtOpenRef = useRef(transactions);

  const selectionTotal = useMemo(() => {
    const selected = new Set(selectedIdsAtOpen);
    return (transactionsAtOpenRef.current ?? [])
      .filter((transaction) => transaction.id !== undefined && selected.has(transaction.id))
      .reduce((sum, transaction) => sum + transaction.amount, 0);
  }, [selectedIdsAtOpen]);

  // A batch date + type can surface as several groups (payeeType splits them), so fold them
  // together before offering them as one target.
  const batchOptions = useMemo(() => {
    const byKey = new Map<string, { batchDate: IsoDateString; batchType: BatchType; total: number }>();
    for (const group of batchGroups ?? []) {
      // The backend rejects a move whose target date matches the source batch's date, regardless of
      // type — so exclude on date alone, or the option is offered and 400s after the confirm step.
      if (group.batchDate === sourceBatchDate) continue;
      const key = toBatchKey(group.batchDate, group.batchType);
      const merged = byKey.get(key);
      if (merged) {
        merged.total += group.transactionSum;
      } else {
        byKey.set(key, { batchDate: group.batchDate, batchType: group.batchType, total: group.transactionSum });
      }
    }
    return [...byKey.values()]
      .toSorted((a, b) => a.batchDate.localeCompare(b.batchDate))
      .map(({ batchDate, batchType, total }) => ({
        value: toBatchKey(batchDate, batchType),
        label: `${DateTime.fromISO(batchDate).toFormat(BATCH_DATE_DISPLAY_FORMAT)} · ${batchTypeLabel(
          batchType,
        )} · ${currencyFormatter.format(total)}`,
      }));
  }, [batchGroups, sourceBatchDate]);

  // Shared by the form body and the footer's submit gate, so both agree on when an existing batch is
  // a selectable target.
  const batchSelect = useMemo(
    () => batchSelectStatus(isLoadingBatchGroups, hasBatchGroupsError, batchOptions.length),
    [isLoadingBatchGroups, hasBatchGroupsError, batchOptions.length],
  );

  // Counted by date alone to match what a move actually drains: `batchType` is absent from the URL on
  // a hand-edited or stale link, and matching on it there would silently zero the count.
  const sourceTransactionCount = useMemo(
    () =>
      (batchGroups ?? [])
        .filter((group) => group.batchDate === sourceBatchDate)
        .reduce((sum, group) => sum + group.transactionCount, 0),
    [batchGroups, sourceBatchDate],
  );

  // Best-effort warning only: with a filter applied the table shows a subset, so a selection that
  // looks partial may still drain the batch. The post-move refetch is what actually confirms it.
  const sourceWillEmpty = Boolean(
    sourceBatchDate && sourceTransactionCount > 0 && selectedIdsAtOpen.length >= sourceTransactionCount,
  );

  // Off a batch detail page the transactions may not be batched yet, so "move" would be wrong.
  const isMovingFromBatch = Boolean(sourceBatchDate);
  // Same wording whether or not the source is a batch: accounting knows this action by one name.
  // `isMovingFromBatch` still drives the toasts and the will-empty warning, which describe what
  // actually happened rather than naming the action.
  const modalTitle = 'Add Transactions to Batch';
  const confirmCopy = 'Confirm — Add to Batch';

  const initialValues: AddToBatchTargetFormValues = useMemo(
    () => ({
      target: 'existing',
      existingBatchKey: '',
      batchType: sourceBatchType ?? '',
      batchDate: '',
      autoFill: false,
      cutoffDateTimeIso: '',
    }),
    [sourceBatchType],
  );

  const closeConfirmation = useCallback(() => {
    setPendingPayload(undefined);
    setPreview(undefined);
  }, []);

  const onSubmit = useCallback(
    async (values: AddToBatchTargetFormValues, { setSubmitting }: FormikHelpers<AddToBatchTargetFormValues>) => {
      const target =
        values.target === 'existing'
          ? fromBatchKey(values.existingBatchKey)
          : {
              batchDate: DateTime.fromFormat(values.batchDate, DATE_FORMAT).toISODate() as IsoDateString | null,
              batchType: values.batchType,
            };

      if (!target.batchDate || !target.batchType) {
        setSubmitting(false);
        return;
      }
      const targetBatchDate = target.batchDate;
      const targetBatchType = target.batchType;

      try {
        let result: AdhocBatchPreviewResult;
        let payload: CreateAdhocBatchPayload;

        if (values.target === 'new' && values.autoFill) {
          const cutoffDateTimeIso = (DateTime.fromISO(values.cutoffDateTimeIso).toUTC().toISO() ??
            '') as IsoDateTimeString;
          result = await previewAdhocBatch({
            batchType: targetBatchType,
            batchDate: targetBatchDate,
            cutoffDateTimeIso,
          }).unwrap();
          payload = { batchType: targetBatchType, batchDate: targetBatchDate, cutoffDateTimeIso };
          setConfirmLabels({
            sourceLabel: 'Unbatched Transactions',
            sourceDetail: `Auto-filled up to ${DateTime.fromISO(cutoffDateTimeIso).toFormat(CUTOFF_DISPLAY_FORMAT_WITH_TZ)}`,
            targetLabel: DateTime.fromISO(targetBatchDate).toFormat(BATCH_DATE_DISPLAY_FORMAT),
            targetDetail: batchTypeLabel(targetBatchType),
          });
        } else {
          if (!selectedIdsAtOpen.length) {
            toast.error('Select at least one transaction before adding to a batch.');
            setSubmitting(false);
            return;
          }
          result = await previewAdhocBatch({
            transactionIds: selectedIdsAtOpen,
            targetBatchDate,
          }).unwrap();
          // `batchType` is required by the endpoint but ignored for a transaction selection — the
          // backend re-derives it per transaction from payee type and payout event.
          payload = { batchType: targetBatchType, batchDate: targetBatchDate, transactionIds: selectedIdsAtOpen };
          setConfirmLabels({
            // Off a batch detail page the source is whatever list the user was looking at, which may
            // hold both batched and unbatched rows — so name the selection, not the list.
            sourceLabel: sourceBatchDate
              ? DateTime.fromISO(sourceBatchDate).toFormat(BATCH_DATE_DISPLAY_FORMAT)
              : 'Selected Transactions',
            sourceDetail: sourceBatchType
              ? `${batchTypeLabel(sourceBatchType)} · ${selectedIdsAtOpen.length} selected`
              : `${selectedIdsAtOpen.length} selected`,
            targetLabel: DateTime.fromISO(targetBatchDate).toFormat(BATCH_DATE_DISPLAY_FORMAT),
            targetDetail: batchTypeLabel(targetBatchType),
          });
        }

        setPreview(result);
        setPendingPayload(payload);
      } catch (error) {
        console.error(error);
        if (isErrorWithErrors(error) || isErrorWithData(error)) {
          toast.error(error.data.message);
        } else {
          toast.error('Could not preview the batch change');
        }
      } finally {
        setSubmitting(false);
      }
    },
    [previewAdhocBatch, selectedIdsAtOpen, sourceBatchDate, sourceBatchType],
  );

  const confirmMove = useCallback(async () => {
    if (!pendingPayload) return false;
    try {
      await createAdhocBatch(pendingPayload).unwrap();
      const movedCount = pendingPayload.transactionIds?.length ?? preview?.summary.transactionCount ?? 0;
      toast.success(
        `${movedCount} transaction${movedCount === 1 ? '' : 's'} ` +
          `${isMovingFromBatch ? 'moved' : 'added'} to the ` +
          `${confirmLabels.targetLabel} ${confirmLabels.targetDetail} batch`,
      );
      closeConfirmation();
      handleClose();
      onSuccess?.();
      return true;
    } catch (error) {
      console.error(error);
      if (isErrorWithErrors(error) || isErrorWithData(error)) {
        toast.error(error.data.message);
      } else {
        toast.error(`Could not ${isMovingFromBatch ? 'move' : 'add'} transactions to batch`);
      }
      return false;
    }
  }, [
    pendingPayload,
    createAdhocBatch,
    preview,
    isMovingFromBatch,
    confirmLabels,
    closeConfirmation,
    handleClose,
    onSuccess,
  ]);

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      <AddToBatchTargetModalContent
        isOpen={isOpen}
        modalTitle={modalTitle}
        confirmCopy={confirmCopy}
        batchOptions={batchOptions}
        batchSelect={batchSelect}
        selectionCount={selectedIdsAtOpen.length}
        selectionTotal={selectionTotal}
        preview={preview}
        confirmLabels={confirmLabels}
        sourceWillEmpty={sourceWillEmpty}
        isConfirmationOpen={isConfirmationOpen}
        isPreviewing={isPreviewing}
        isCreating={isCreating}
        closeConfirmation={closeConfirmation}
        handleClose={handleClose}
        confirmMove={confirmMove}
      />
    </Formik>
  );
};
